<script setup lang="ts">
import type { Option } from '#imports'
import { isTest } from '#imports'

const props = defineProps<{
  variant?: Variant
  mode?: 'buy' | 'add'
  displaySymbol?: boolean
  hidesSelectedOptions?: boolean
}>()

const emit = defineEmits<{
  (event: 'select', option: Option): void
}>()

const optionsDialog = ref()

const mediaQuery = useMediaQuery()

const isSoldOut = computed(() => {
  if (isTest()) {
    return false
  }

  const optionsWithStock = (props.variant ? (options.value ?? []) : []).filter(
    (option: Option) => !(option.stock === 0),
  )
  return optionsWithStock.length === 0
})

const options = computed(() => {
  return (props.variant?.options ?? []).filter((option: Option) => true) // !props.hidesSelectedOptions || !order.isOptionSelected(option));
})

const lowestOptions = computed(() => {
  return getLowestDiscountOption(props.variant?.options)
})

function openOptionPicker() {
  // do we have options
  if (options.value.length === 0) {
    return
  }

  // only one option?
  if (options.value.length === 1) {
    selectOption(options.value[0])
    return
  }

  // show the option picker
  optionsDialog.value.open()
}

function selectOption(option: Option) {
  optionsDialog.value &&
    optionsDialog.value.isOpen &&
    optionsDialog.value.close()
  emit('select', option)
}

function getLowestDiscountOption(options: Option[]): Option | null {
  return options.reduce((lowest: Option | null, option: Option) => {
    return lowest === null ||
      parseFloat(option.discountPrice) < parseFloat(lowest.discountPrice)
      ? option
      : lowest
  }, null)
}
</script>

<template>
  <ion-card
    v-if="variant"
    class="os-variant-card"
    :class="{ 'os-variant-card--compact': !mediaQuery.isSmall }"
    @click="openOptionPicker"
    :inert="isSoldOut"
  >
    <os-row cross-axis-alignment="stretch" gap="none">
      <img
        v-if="variant?.image"
        class="os-variant-card__image deal-image-ratio"
        :alt="variant.name"
        :src="variant.image.lg"
        :style="mediaQuery.isSmall ? 'width: 25%' : 'width: 40%'"
      />

      <os-expanded>
        <os-column
          cross-axis-alignment="stretch"
          gap="narrow"
          class="os-variant-card__details"
        >
          <ion-card-title>
            <os-clamp :lines="2">{{ stripTags(variant.name) }}</os-clamp>
            <div
              v-if="options.length > 1"
              class="os-variant-card__option-count"
            >
              {{
                $t('deal.option_count', options.length, {
                  count: options.length,
                })
              }}
            </div>
            <div
              v-else-if="options[0] && options[0].name !== variant.name"
              class="os-variant-card__option-count"
            >
              {{ stripTags(options[0].name) }}
            </div>
          </ion-card-title>

          <os-expanded />

          <template v-if="mediaQuery.isLarge">
            <os-row gap="narrow" cross-axis-alignment="end">
              <os-column cross-axis-alignment="start" gap="none">
                <span v-if="variant.isOpenGds">{{ $t('deal.from') }}</span>
                <s
                  class="h3"
                  v-if="
                    variant.originalPrice &&
                    parseFloat(variant.originalPrice) >
                      parseFloat(variant.discountPrice)
                  "
                  ><os-currency
                    :value="
                      lowestOptions
                        ? lowestOptions.originalPrice
                        : variant.originalPrice
                    "
                    :display-symbol="displaySymbol"
                /></s>
                <div class="h1">
                  <os-currency
                    :value="
                      lowestOptions
                        ? lowestOptions.discountPrice
                        : variant.discountPrice
                    "
                    :display-symbol="displaySymbol"
                  />
                </div>
              </os-column>

              <os-expanded />

              <ion-button
                secondary
                inert
                size="small"
                v-if="variant.percentOff && !isSoldOut"
              >
                <b><os-percent :value="variant.percentOff * -1" /></b>
              </ion-button>

              <ion-button size="small" v-if="mode">
                <template v-if="isSoldOut" />
                <ion-icon
                  v-else-if="mode === 'add'"
                  slot="end"
                  :icon="ioniconsAddOutline"
                ></ion-icon>
                <ion-icon
                  v-else-if="mode === 'buy'"
                  slot="end"
                  :icon="ioniconsChevronForward"
                ></ion-icon>
                <b v-if="isSoldOut">{{ $t('label.sold_out') }}</b>
                <b v-else-if="mode === 'add'">{{
                  $t(variant?.isOpenGds ? 'label.book' : 'label.add')
                }}</b>
                <b v-else-if="mode === 'buy'">{{
                  $t(variant?.isOpenGds ? 'label.book' : 'label.order_now')
                }}</b>
              </ion-button>
            </os-row>
          </template>

          <template v-else>
            <os-row
              main-axis-alignment="space-between"
              cross-axis-alignment="center"
              gap="none"
            >
              <os-expanded>
                <os-row
                  cross-axis-alignment="center"
                  main-axis-alignment="start"
                  gap="narrow"
                >
                  <span v-if="variant.isOpenGds">{{ $t('deal.from') }}</span>
                  <s
                    class="h3"
                    v-if="
                      variant.originalPrice &&
                      parseFloat(variant.originalPrice) >
                        parseFloat(variant.discountPrice)
                    "
                    ><os-currency
                      :value="
                        lowestOptions
                          ? lowestOptions.originalPrice
                          : variant.originalPrice
                      "
                      :display-symbol="displaySymbol"
                  /></s>
                  <div class="h1">
                    &nbsp;<os-currency
                      :value="
                        lowestOptions
                          ? lowestOptions.discountPrice
                          : variant.discountPrice
                      "
                      :display-symbol="displaySymbol"
                    />
                  </div>
                </os-row>
              </os-expanded>

              <os-column
                class="os-variant-card__percent-off"
                v-if="variant.percentOff && !isSoldOut"
              >
                <os-percent :value="variant.percentOff * -1" />
              </os-column>
            </os-row>

            <ion-button size="small" v-if="mode">
              <template v-if="isSoldOut" />
              <ion-icon
                v-else-if="mode === 'add'"
                slot="end"
                :icon="ioniconsAddOutline"
              ></ion-icon>
              <ion-icon
                v-else-if="mode === 'buy'"
                slot="end"
                :icon="ioniconsChevronForward"
              ></ion-icon>
              <b v-if="isSoldOut">{{ $t('label.sold_out') }}</b>
              <b v-else-if="mode === 'add'">{{
                $t(variant?.isOpenGds ? 'label.book' : 'label.add')
              }}</b>
              <b v-else-if="mode === 'buy'">{{
                $t(variant?.isOpenGds ? 'label.book' : 'label.order_now')
              }}</b>
            </ion-button>
          </template>
        </os-column>
      </os-expanded>
    </os-row>

    <os-dialog ref="optionsDialog" :title="$t('label.make_your_choice')">
      <os-column
        cross-axis-alignment="stretch"
        :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
        class="os-variant-card__options-list"
      >
        <os-option-card
          v-for="option in options ?? []"
          :option="option"
          :mode="mode ?? 'buy'"
          :display-symbol="displaySymbol"
          @select="selectOption(option)"
        />
      </os-column>
    </os-dialog>
  </ion-card>
</template>

<style scoped>
.os-variant-card {
  background-color: var(--background--primary);
  cursor: pointer;
}

.os-variant-card[inert] {
  opacity: 0.5;
}

.os-variant-card > .os-row {
  height: 100%;
}

.os-variant-card__image {
  object-fit: contain;
  background-color: white;
  display: flex;
  flex: 0 0 0%;
  flex-basis: auto;
}

.os-variant-card__details {
  padding: 1rem;
}

.os-variant-card--compact .os-variant-card__details {
  padding: 0.75rem 1rem;
}

.os-variant-card__option-count {
  opacity: 0.75;
  font-weight: normal;
}

.os-variant-card__options-list {
  padding-bottom: 2rem;
}

.os-variant-card__percent-off {
  background-color: var(--header);
  color: var(--white);
  border-radius: 0.25rem;
  padding: 5px;
  flex-shrink: 1;
  flex-grow: 0;
  font-size: 0.65rem;
  font-weight: bold;
}
</style>
